import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { ApplicationCode } from '@ph-env/model/applications.models';
import { notificationDefaultDuration } from '@ph-static-data/notification-durations';
import { filter } from 'rxjs/operators';

import { ConfigService } from '@ph-core/services/config/config.service';
import { ErrorService } from '@ph-core/services/error/error.service';
import { RequestTrackerService } from '@ph-core/services/request-tracker.service';
import { BigDotsSpinnerComponent } from '@ph-shared/components/big-dots-spinner/big-dots-spinner.component';
import { LoaderComponent } from '@ph-shared/components/loader/loader.component';
import { PhNotificationType } from '@ph-shared/components/notification/notification/notification.interface';
import { ConfigStore, ConfigStoreType } from '@ph-store/config/config.store';
import { PrismicStore, PrismicStoreType } from '@ph-store/prismic/prismic.store';
import { applyClientCssValues } from '@ph-shared/utils/client-css-setter.util';

import { NotificationService } from './core/services/notification';
import { SupportedLanguageCodes } from './static-data/languages';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, LoaderComponent, BigDotsSpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  configStore: ConfigStoreType = inject(ConfigStore);
  initialLoading: Signal<boolean> = this.configStore.initialLoading;
  loading: Signal<boolean> = computed(
    () =>
      this.requestTrackerService.loading() || this.configStore.templateServiceLoading()
  );
  prismicStore: PrismicStoreType = inject(PrismicStore);

  private titleService = inject(Title);
  private errorService = inject(ErrorService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private configService = inject(ConfigService);
  private requestTrackerService = inject(RequestTrackerService);

  constructor() {
    this.prismicStore.loadPrismicData();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const languageCode = localStorage.getItem('languageCode') || SupportedLanguageCodes.EnglishCA;
        if (this.translate.currentLang && this.translate.currentLang !== languageCode) {
          this.translate.use(languageCode);
          // For cases when currentLang is not defined yet, happens on page refresh
        } else if (!this.translate.currentLang) {
          setTimeout(() => this.translate.use(languageCode));
        }
      });

    effect(() => {
      const err = this.errorService.error();

      if (err.show && !err.ssoScenario) {
        this.notificationService.push({
          value: { ...err, title: 'Warning!' },
          type: PhNotificationType.denied,
          duration: err.duration || notificationDefaultDuration,
        });
      }
    });

    effect(() => {
      const faviconUrl = this.prismicStore.data()?.favicon?.url;
      const favIcon = document.querySelector('#metaFavicon');
      if (favIcon && 'href' in favIcon) {
        favIcon.href = faviconUrl;
      }
    });
  }

  ngOnInit(): void {
    applyClientCssValues(this.configService.environment.colors);
    this.configStore.getInitialLayout({ application: this.configService.environment.application });
    this._setTitle(this.configService.environment.application);
  }

  private _setTitle(application: ApplicationCode): void {
    let title: string;
    switch (application) {
      case 'extraprotectca':
        title = 'Extra Protect Canada';
        break;
      case 'lfsprotectionca':
        title = 'LFS Protection';
        break;
      case 'tfsprotectionca':
        title = 'TFS Protection';
        break;
      case 'sfsprotectionca':
        title = 'SFS Protection';
        break;
      case 'astonmartinppcom':
        title = 'Aston Martin Protection';
        break;
      case 'caregapacuracom':
        title = 'Care Gap Acura Protection';
        break;
      case 'caregaphondacom':
        title = 'Care Gap Honda Protection';
        break;
      case 'cccdealerportalcom':
        title = 'CCC Dealer Protection';
        break;
      case 'extraprotectcom':
        title = 'Extra Protect US';
        break;
      case 'findlay-dealersextraprotectcom':
        title = 'Findlay Dealers Extra Protect';
        break;
      case 'lithia':
        title = 'Lithia';
        break;
      case 'firstclassprotection':
        title = 'First Class Protection';
        break;
      case 'gmprotectioncom':
        title = 'GM Protection';
        break;
      case 'lfsdealerportalcom':
        title = 'LFS Dealer Protection';
        break;
      case 'moparvehicleprotectionca':
        title = 'Mopar Vehicle Protection Canada';
        break;
      case 'nomad':
        title = 'Nomad Protection Plans';
        break;
      case 'powerprotectdealercom':
      case 'wldealer':
        title = 'Power Protect Dealer';
        break;
      case 'primeautomotiveprotectcom':
        title = 'Prime Automotive Protection';
        break;
      case 'tfsdealerportalcom':
        title = 'TFS Dealer Portal';
        break;
      case 'youroneprotectcom':
        title = 'Your One Protect';
        break;
      case 'sginternal':
        title = 'SG Internal Portal';
        break;

      //pbl
      case 'porscheprotectionplan':
        title = 'Porsche Protection';
        break;
      case 'bentleyprotectionplan':
        title = 'Bentley Protection';
        break;
      case 'lamborghiniprotectionplan':
        title = 'Lamborghini Protection';
        break;

      //sgagents
      case 'sgagents':
        title = 'SG Agents Portal';
        break;

      //vci
      case 'audi':
        title = 'AUDI Portal';
        break;
      case 'ducati':
        title = 'Ducati Portal';
        break;
      case 'vw':
        title = 'VW Portal';
        break;
      case 'qualityprotection':
        title = 'Quality Protection Portal';
        break;

      //bmw motorrad
      case 'bmwmotorradus':
        title = 'BMW Motorrad US Portal';
        break;

      //hci
      case 'hppdealer':
        title = 'Hyundai Protection Plan';
        break;
      case 'gppdealer':
        title = 'Genesis Protection Plan';
        break;
      case 'ppesdealer':
        title = 'PPES Protection Plan';
        break;
      case 'kpsdealer':
        title = 'KPS Protection Plan';
        break;
      case 'hgcareconnect':
        title = 'HG Consumer Affairs';
        break;

      case 'subaruprotectionplanca':
        title = 'SPP Portal';
        break;

      default:
        title = 'Title should be updated';
        break;
    }
    this.titleService.setTitle(title);
  }
}
